import React, { FC, MutableRefObject, ReactElement, useContext, useEffect } from 'react'
import moment from 'moment'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { InsightsDashboardLanguage } from '../../../../../i18n'
import { SelectOptions } from '../../../../React/Common/Components/Selects'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { useFormContext, UseFormMethods, useWatch } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { PARTITION_FILTER_NAME, TELSYS_FILTER_NAME } from '../../../../React/InsightsHub/Constants/FieldNames'
import {
    ALL_VALUE,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions
} from '../../../../React/Common/API/ItemsGetters'
import { DEFAULT_CME_ID } from '../../../../React/AddUser/Constants'
import { handleUnknownForAvailables } from '../../../../React/AddUser/AddUserFunctions'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { IsVarEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { SelectOption } from '../../../../React/Common/Components/Selects/Types'
import { COMMON_DEVICES_PARTITION_NAME } from '../../../../NonReact/constants/Common'
import { ComboBox } from '../../../../React/Common/Components/ComboBox/ComboBox'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { InsightsStorage } from '../../../../React/InsightsHub/Types/Types'
import { StateSetter } from '../../../../React/Common/Types/Types'

type Props = {
    isDataLoading: boolean
    isLoadingTelServers: ReactState<boolean>
    isLoadingPartitions: ReactState<boolean>
    insightsStorage: Nullable<InsightsStorage>
    setErrorMessage: StateSetter<Nullable<string>>
    availableTelServers: ReactState<Definable<SelectOptions<number>>>
    availablePartitions: ReactState<Definable<SelectOptions<number>>>
}

export const InsightsTopBar: FC<Props> = ({
    isDataLoading,
    isLoadingPartitions: [isLoadingPartitions, setIsLoadingPartitions],
    isLoadingTelServers: [isLoadingTelServers, setIsLoadingTelServers],
    insightsStorage,
    setErrorMessage,
    availableTelServers: [availableTelServers, setAvailableTelServers],
    availablePartitions: [availablePartitions, setAvailablePartitions]
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { setValue }: UseFormMethods = useFormContext()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const [userInfo]: UserInfo = useUserInfo()

    const telSysValue: number = useWatch<number>({
        name: TELSYS_FILTER_NAME,
        defaultValue: availableTelServers ? availableTelServers[0]?.value : ALL_VALUE
    })

    const partitionValue: Definable<number> = useWatch<Definable<number>>({
        name: PARTITION_FILTER_NAME
    })

    useEffect((): void => {
        if (userInfo && (IsVarEmpty(availableTelServers) || availableTelServers.length === 0)) {
            setIsLoadingTelServers(true)
            setValue(PARTITION_FILTER_NAME, undefined)

            getTelServersAsSelectOptions(userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID, true)
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setAvailablePartitions(undefined)
                        setValue(PARTITION_FILTER_NAME, undefined)

                        if (
                            insightsStorage?.telSys &&
                            telServers.some(
                                (telServer: SelectOption<number>): boolean => telServer.value === insightsStorage.telSys
                            )
                        ) {
                            setValue(TELSYS_FILTER_NAME, insightsStorage?.telSys)
                        } else if (
                            userInfo?.Scope?.TelSysID &&
                            userInfo.Scope.TelSysID.length > 0 &&
                            userInfo.Scope.TelSysID[0] === ALL_VALUE &&
                            telServers.length > 0
                        ) {
                            setValue(TELSYS_FILTER_NAME, telServers?.[0]?.value)
                        } else {
                            setValue(TELSYS_FILTER_NAME, userInfo.Scope.TelSysID[0])
                        }

                        handleUnknownForAvailables(telServers, setAvailableTelServers, telSysValue)
                    }
                })
                .catch((error: Error): void => {
                    displayError(error)
                    setErrorMessage(error.message)
                })
                .finally((): void => setIsLoadingTelServers(false))
        }
    }, [userInfo, JSON.stringify(availableTelServers)])

    useEffect((): void => {
        if (telSysValue && telSysValue !== ALL_VALUE) {
            setIsLoadingPartitions(true)
            getPartitionsForTelServer(telSysValue)
                .then(
                    (partitions: SelectOptions<number>): SelectOptions<number> =>
                        partitions.filter(
                            (partition: SelectOption<number>): boolean =>
                                partition.label !== COMMON_DEVICES_PARTITION_NAME
                        )
                )
                .then((partitions: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        const isSinglePartitionUser: boolean =
                            (userInfo?.Scope?.PartIDInTelSys?.length ?? 0) > 0 &&
                            userInfo?.Scope.PartIDInTelSys[0] !== ALL_VALUE

                        if (
                            insightsStorage?.partition &&
                            partitions.some(
                                (partition: SelectOption<number>): boolean =>
                                    partition.value === insightsStorage.partition
                            ) &&
                            telSysValue === insightsStorage?.telSys
                        ) {
                            setValue(PARTITION_FILTER_NAME, insightsStorage?.partition)
                        } else if (isSinglePartitionUser) {
                            setValue(PARTITION_FILTER_NAME, partitions.find((): boolean => true)?.value ?? 0)
                        } else {
                            setValue(PARTITION_FILTER_NAME, ALL_VALUE)
                        }

                        handleUnknownForAvailables(
                            isSinglePartitionUser
                                ? partitions
                                : [
                                      {
                                          label: language.get(
                                              InsightsDashboardLanguage.InsightsDashboard_AllPartitions
                                          ),
                                          value: ALL_VALUE
                                      },
                                      ...partitions
                                  ],
                            setAvailablePartitions,
                            partitionValue ?? ALL_VALUE
                        )
                    }
                })
                .catch((e: Error): void => {
                    displayError(e)
                    setErrorMessage(e.message)
                })
                .finally((): void => setIsLoadingPartitions(false))
        }
    }, [telSysValue])

    return (
        <div className='insd-top-bar'>
            <div className='idtb-left'>
                <h1 className='idtbl-title'>{language.get(InsightsDashboardLanguage.InsightsDashboard_Title)}</h1>
                <h2 className='idtbl-date'>{moment().format('Do MMMM YYYY')}</h2>
            </div>
            {availableTelServers && availablePartitions ? (
                <div className='idtb-right'>
                    <div className='idtbr-business-filter-title'>
                        {language.get(InsightsDashboardLanguage.InsightsDashboard_BusinessFilter)}
                    </div>
                    <div className='idtbr-business-filters'>
                        <ComboBox
                            label=''
                            variant='outlined'
                            name={TELSYS_FILTER_NAME}
                            defaultOption={availableTelServers[0]}
                            options={availableTelServers.slice(1)}
                            disabled={
                                isDataLoading ||
                                isLoadingTelServers ||
                                (userInfo?.Scope?.TelSysID?.length === 1 && userInfo?.Scope?.TelSysID[0] !== ALL_VALUE)
                            }
                            onChange={(): void => setValue(PARTITION_FILTER_NAME, undefined)}
                        />
                        {!ArrayUtils.isEmpty(availablePartitions) ? (
                            <ComboBox
                                label=''
                                variant='outlined'
                                name={PARTITION_FILTER_NAME}
                                defaultOption={availablePartitions[0]}
                                options={availablePartitions.slice(1)}
                                disabled={
                                    isDataLoading ||
                                    isLoadingPartitions ||
                                    (userInfo?.Scope?.PartIDInTelSys?.length === 1 &&
                                        userInfo?.Scope?.PartIDInTelSys[0] !== ALL_VALUE)
                                }
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
